import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFile,
    faFileExcel,
    faFileImage,
    faFileLines,
    faFilePdf,
    faFileWord,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from './button';

const fileIcons = {
    pdf: faFilePdf,
    doc: faFileWord,
    docx: faFileWord,
    xls: faFileExcel,
    xlsx: faFileExcel,
    csv: faFileExcel,
    png: faFileImage,
    jpg: faFileImage,
    jpeg: faFileImage,
    txt: faFileLines,
    md: faFileLines,
};

// Both separators, because the paths are produced server-side and the backend
// runs on Windows as well as Linux.
export const getFileNameFromPath = (filePath) =>
    typeof filePath === 'string' ? filePath.split(/[/\\]/).pop() : '';

/**
 * A row of click-to-download file buttons.
 *
 * Shared by a question's attachments (QnaMetadata.attachments) and an answer's
 * generated files (the Answer envelope's `files`), and by both the web app and
 * the productSupport widget - which download through different endpoints,
 * hence `onDownload` rather than a URL built in here.
 *
 * Presentational only: the caller owns the request, the error reporting and
 * `loadingPath` (the path currently downloading, or null).
 */
export const FileDownloadList = ({
    files,
    onDownload,
    loadingPath,
    disabled,
    className = '',
}) => {
    const paths = Array.isArray(files) ? files.filter(Boolean) : [];

    if (paths.length === 0) {
        return null;
    }

    return (
        <div className={`mt-3 flex flex-wrap gap-2 ${className}`}>
            {paths.map((filePath, idx) => {
                const fileName =
                    getFileNameFromPath(filePath) || `file-${idx + 1}`;
                const ext = fileName.split('.').pop()?.toLowerCase();
                const icon = fileIcons[ext] || faFile;
                const isLoading = loadingPath === filePath;

                return (
                    <Button
                        key={`${fileName}-${idx}`}
                        onClick={() => onDownload(filePath)}
                        disabled={isLoading || disabled}
                        type='info'
                        className='
                            !bg-backgrounds-secondaryChat
                            !border-borders-secondary200
                            min-w-[150px] max-w-[180px]
                            h-[46px]
                            flex items-center gap-2
                            shadow-inner
                            hover:scale-105
                            transition
                            overflow-hidden
                        '
                        text={
                            <div
                                className='flex items-center gap-2 w-full'
                                title={fileName}
                            >
                                <FontAwesomeIcon
                                    icon={icon}
                                    className='text-texts-primary400 text-sm'
                                />
                                <div className='flex flex-col flex-1 leading-tight overflow-hidden'>
                                    <span
                                        className='font-semibold text-sm text-texts-primary400 truncate block'
                                        style={{ maxWidth: '110px' }}
                                    >
                                        {fileName}
                                    </span>
                                    <span className='text-[10px] text-texts-secondary300 truncate'>
                                        {isLoading
                                            ? 'Downloading…'
                                            : 'Click to download'}
                                    </span>
                                </div>
                            </div>
                        }
                    />
                );
            })}
        </div>
    );
};
